import {
    FileType,
    FILE_TYPE_EXTENSIONS,
} from "../constants/financial-documents";
import { FinancialAccountType } from "../types/financialAccount";
import {
    DocumentTaxonomy,
    RECEIPT_MATCHABLE_TAXONOMIES,
} from "../documentCategories";
import {
    DocumentTaxonomyExtraction,
    FinancialDocumentExtended,
    ImporterErrors,
    ReceiptData,
    ReceiptOCRResponse,
} from "../types/financialDocument";
import { getUTCDateForPST, toIsoDate } from "./date";
import { fromMonetaryToInt } from "./monetary";
import { isLiabilityAccount } from "./financialAccount";

interface DoesBalancesAndTransactionsReconcileParams {
    openingBalance: number;
    closingBalance: number;
    moneyIn: number;
    moneyOut: number;
    financialType: FinancialAccountType;
}

export function doesBalancesAndTransactionsReconcile({
    closingBalance,
    openingBalance,
    moneyIn,
    moneyOut,
    financialType,
}: DoesBalancesAndTransactionsReconcileParams) {
    if (isLiabilityAccount(financialType)) {
        moneyOut = -moneyOut;
        moneyIn = -moneyIn;
    }

    return (
        fromMonetaryToInt(openingBalance) +
            fromMonetaryToInt(moneyIn) -
            fromMonetaryToInt(moneyOut) ===
        fromMonetaryToInt(closingBalance)
    );
}

export const IMPORTER_TRANSACTIONS_QUEUE = "transactions_import";

export const IMPORTER_INVOICES_QUEUE = "invoices_import";

export const IMPORTER_BILLS_QUEUE = "bills_import";

export const RECONCILIATION_QUEUE_PREFIX = "reconciliation_";

export function getImporterReconciliationQueue(financialAccountId: number) {
    return `${RECONCILIATION_QUEUE_PREFIX}${financialAccountId}`;
}

export function isImporterReconciliationQueue(queue: string) {
    return queue.startsWith(RECONCILIATION_QUEUE_PREFIX);
}

export function canReassignLinkedReconciliationAccount({
    isReconciled,
    importerQueue,
}: {
    isReconciled: boolean;
    importerQueue: string | null;
}): boolean {
    return (
        !isReconciled &&
        importerQueue != null &&
        isImporterReconciliationQueue(importerQueue)
    );
}

export function isImporterTransactionsQueue(queue: string): boolean {
    return queue === IMPORTER_TRANSACTIONS_QUEUE;
}

export function isImporterAccrualQueue(queue: string): boolean {
    return queue === IMPORTER_INVOICES_QUEUE || queue === IMPORTER_BILLS_QUEUE;
}

// Allowlist a new queue must opt into; unknown queues skip the pipeline.
export function importerQueueRunsTransactionPipeline(queue: string): boolean {
    return (
        isImporterTransactionsQueue(queue) ||
        isImporterReconciliationQueue(queue)
    );
}

export type ImporterQueueKind =
    | "transactions"
    | "reconciliation"
    | "invoices"
    | "bills";

export function getImporterQueueKind(queue: string): ImporterQueueKind | null {
    if (isImporterTransactionsQueue(queue)) {
        return "transactions";
    }
    if (isImporterReconciliationQueue(queue)) {
        return "reconciliation";
    }
    if (queue === IMPORTER_INVOICES_QUEUE) {
        return "invoices";
    }
    if (queue === IMPORTER_BILLS_QUEUE) {
        return "bills";
    }
    return null;
}

export function getReconciliationQueueAccountId(queue: string): number | null {
    if (!isImporterReconciliationQueue(queue)) {
        return null;
    }
    const idPart = queue.slice(RECONCILIATION_QUEUE_PREFIX.length);
    if (!/^[1-9]\d*$/.test(idPart)) {
        return null;
    }
    const id = Number(idPart);
    if (!Number.isSafeInteger(id) || id > 2_147_483_647) {
        return null;
    }
    return id;
}

export enum ImporterSpreadsheetColumns {
    DATE = "date",
    DESCRIPTION = "description",
    AMOUNT = "amount",
}

export function canRevertFinancialDocumentRename<
    T extends {
        fileName: string;
        originalFileName: string | null;
    },
>(document: T): document is T & { originalFileName: string } {
    return (
        document.originalFileName != null &&
        document.fileName !== document.originalFileName
    );
}

interface AlignReceiptTaxonomyPeriodParams {
    taxonomyExtraction: DocumentTaxonomyExtraction | null;
    receiptDate: Date | null | undefined;
    documentTaxonomy: DocumentTaxonomy | null | undefined;
}

export function alignReceiptTaxonomyPeriodWithReceiptDate({
    taxonomyExtraction,
    receiptDate,
    documentTaxonomy,
}: AlignReceiptTaxonomyPeriodParams): DocumentTaxonomyExtraction | null {
    if (!taxonomyExtraction || !receiptDate || !documentTaxonomy) {
        return taxonomyExtraction;
    }

    if (!RECEIPT_MATCHABLE_TAXONOMIES.has(documentTaxonomy)) {
        return taxonomyExtraction;
    }

    const receiptDateOnly = toIsoDate(receiptDate);

    if (
        taxonomyExtraction.periodStartDate === receiptDateOnly &&
        taxonomyExtraction.periodEndDate === receiptDateOnly
    ) {
        return taxonomyExtraction;
    }

    return {
        ...taxonomyExtraction,
        periodStartDate: receiptDateOnly,
        periodEndDate: receiptDateOnly,
    };
}

export function parseOCROutput(
    matcherOutput: ReceiptOCRResponse,
): Partial<ReceiptData> {
    const { date, totalAmount, merchantName } = matcherOutput;

    return {
        amount: totalAmount.data,
        date: date.data ? getUTCDateForPST(new Date(date.data)) : undefined,
        merchant: merchantName.data,
    };
}

export function getCashAppExcludedLinkedAccountPaymentCount(
    importerOutput: object | null | undefined,
): number | undefined {
    if (
        !importerOutput ||
        !("cash_app_excluded_linked_account_payment_count" in importerOutput)
    ) {
        return undefined;
    }

    const excludedCount =
        importerOutput.cash_app_excluded_linked_account_payment_count;

    if (
        typeof excludedCount === "number" &&
        Number.isInteger(excludedCount) &&
        excludedCount >= 0
    ) {
        return excludedCount;
    }

    return undefined;
}

export function isConfidentPdfParserImporterOutput(
    importerOutput: object | null | undefined,
): boolean {
    if (
        !importerOutput ||
        !("method" in importerOutput) ||
        !("confident" in importerOutput)
    ) {
        return false;
    }

    return (
        importerOutput.method === "pdf_parsers" &&
        importerOutput.confident === true
    );
}

export const NON_BLOCKING_IMPORTER_ERRORS: ImporterErrors[] = [
    ImporterErrors.CASH_APP_EXCLUDED_LINKED_PAYMENTS,
    ImporterErrors.ACCRUAL_POSSIBLE_DUPLICATE,
];

export const DISMISSABLE_IMPORTER_ERRORS: ImporterErrors[] = [
    ImporterErrors.RECONCILED_WITH_INDETERMINABLE_SIGNS,
    ImporterErrors.NOT_CONFIDENT,
    ImporterErrors.SAME_PERIOD_ALREADY_UPLOADED,
    ImporterErrors.DUPLICATE_FILE_UPLOADED,
    ImporterErrors.FOREIGN_CURRENCY,
    ImporterErrors.ASSIGNED_FINANCIAL_ACCOUNT_MISMATCH,
    ImporterErrors.TRANSACTIONS_OUTSIDE_PERIOD,
    ImporterErrors.TRANSACTIONS_BEFORE_BOOKKEEPING_START,
    ImporterErrors.TRANSACTIONS_AFTER_BOOKKEEPING_END,
    ImporterErrors.TRANSACTIONS_AFTER_ACCOUNT_BOOKKEEPING_END,
    ImporterErrors.ACCRUAL_LINE_ITEMS_DONT_SUM_TO_TOTAL,
    ImporterErrors.CASH_APP_EXCLUDED_LINKED_PAYMENTS,
    ImporterErrors.ACCRUAL_WRONG_ENTITY,
    ImporterErrors.ACCRUAL_PARTIALLY_PAID,
    ImporterErrors.ACCRUAL_COUNTERPARTY_DETAILS_CHANGED,
    ImporterErrors.ACCRUAL_NOT_ACCRUAL_DOCUMENT,
    ImporterErrors.ACCRUAL_WRONG_DOCUMENT_TYPE,
];

export function isDismissableImporterError({
    error,
    importerQueue,
}: {
    error: ImporterErrors;
    importerQueue: string | null;
}): boolean {
    const isAccrualForeignCurrency =
        error === ImporterErrors.FOREIGN_CURRENCY &&
        importerQueue !== null &&
        isImporterAccrualQueue(importerQueue);
    return (
        !isAccrualForeignCurrency && DISMISSABLE_IMPORTER_ERRORS.includes(error)
    );
}

interface ImporterErrorsState {
    importerErrors?: ImporterErrors[] | null;
    dismissedImporterErrors?: ImporterErrors[] | null;
}

export function hasActiveImporterError({
    document,
    error,
}: {
    document: ImporterErrorsState;
    error: ImporterErrors;
}): boolean {
    return (
        Boolean(document.importerErrors?.includes(error)) &&
        !document.dismissedImporterErrors?.includes(error)
    );
}

export function hasActiveImporterErrors({
    document,
    ignoredErrors,
}: {
    document: ImporterErrorsState;
    ignoredErrors: ImporterErrors[];
}): boolean {
    return (document.importerErrors ?? []).some(
        (error) =>
            !ignoredErrors.includes(error) &&
            hasActiveImporterError({ document, error }),
    );
}

export function hasBlockingImporterErrors(
    document: ImporterErrorsState,
): boolean {
    return hasActiveImporterErrors({
        document,
        ignoredErrors: NON_BLOCKING_IMPORTER_ERRORS,
    });
}

export function hasBlockingBulkImportErrors(
    document: ImporterErrorsState,
): boolean {
    return hasActiveImporterErrors({ document, ignoredErrors: [] });
}

export const BULK_CONFIRM_COUNTERPARTIES_RESOLVED_ERRORS: ImporterErrors[] = [
    ImporterErrors.ACCRUAL_UNCONFIRMED_COUNTERPARTY,
    ImporterErrors.ACCRUAL_MISSING_ACCOUNT,
    ImporterErrors.ACCRUAL_UNCONFIRMED_ACCOUNT,
    ImporterErrors.ACCRUAL_UNCONFIRMED_ITEM,
];

export function getFinancialAccountType(
    financialDocument: FinancialDocumentExtended,
): FinancialAccountType | null {
    const integrationAccount = financialDocument.integrationAccount;

    if (integrationAccount?.financialAccount?.financialType) {
        return integrationAccount.financialAccount.financialType;
    }

    if (financialDocument.financialAccount?.financialType) {
        return financialDocument.financialAccount.financialType;
    }

    return financialDocument.accountInfoExtraction?.accountType ?? null;
}

export function formatExtensionsForDropzone(
    fileTypes: readonly FileType[],
): string {
    return fileTypes.map((t) => FILE_TYPE_EXTENSIONS[t]).join(",");
}
